import { createSlice, PayloadAction } from "@reduxjs/toolkit"

export interface WsConfig {
  token: string
  tokenSignature: string
  authorizerName: string
  tokenKeyName: string
  iotEndpoint: string
  topics: string[]
  userId: number
  mblsProviderId: number
}

export interface WsToast {
  open: boolean
  title: string
  body: string
  orderId: number | null
}

export interface WsState {
  config: WsConfig | null
  connectionStatus: "disconnected" | "connecting" | "connected" | "error"
  error: string | null
  lastMessage: { topic: string; message: any; timestamp: number } | null
  subscribedTopics: string[]
  userStatus: { lastActivity: number } | null
  toast: WsToast | null
}

const initialState: WsState = {
  config: null,
  connectionStatus: "disconnected",
  error: null,
  lastMessage: null,
  subscribedTopics: [],
  userStatus: null,
  toast: null,
}

export const wsSlice = createSlice({
  name: "ws",
  initialState,
  reducers: {
    setConfig: (state, action: PayloadAction<WsConfig>) => {
      state.config = action.payload
    },
    setConnectionStatus: (
      state,
      action: PayloadAction<WsState["connectionStatus"]>,
    ) => {
      state.connectionStatus = action.payload
      if (action.payload === "connected") {
        state.error = null
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.connectionStatus = "error"
    },
    setLastMessage: (state, action: PayloadAction<WsState["lastMessage"]>) => {
      state.lastMessage = action.payload
    },
    addSubscribedTopic: (state, action: PayloadAction<string>) => {
      if (!state.subscribedTopics.includes(action.payload)) {
        state.subscribedTopics.push(action.payload)
      }
    },
    setSubscribedTopics: (state, action: PayloadAction<string[]>) => {
      state.subscribedTopics = action.payload
    },
    setUserStatus: (state, action: PayloadAction<WsState["userStatus"]>) => {
      state.userStatus = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    showNotificationToast: (
      state,
      action: PayloadAction<{ title: string; body: string; orderId: number | null }>,
    ) => {
      state.toast = { open: true, ...action.payload }
    },
    dismissToast: (state) => {
      state.toast = null
    },
  },
})

export const {
  setConfig,
  setConnectionStatus,
  setError,
  setLastMessage,
  addSubscribedTopic,
  setSubscribedTopics,
  setUserStatus,
  showNotificationToast,
  dismissToast,
} = wsSlice.actions

export default wsSlice.reducer
